import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsRTL } from '../../redux/selectors/displaySettingsSelectors';

// Custom hooks
import { useChartConfig } from './hooks/useChartConfig';

// Shared components
import { ChartContainer } from './shared/ChartContainer';
import { ChartContent } from './shared/ChartContent';
import { ChartFooter } from './shared/ChartFooter';
import { IWeeklyLogAnalyticsResponse } from '../../common/interfaces/WeeklyLogAnalyticsResponse.interface';
import { FooterDivider } from 'flowbite-react';

interface WeeklyLogOverviewTabProps {
  analyticsData: IWeeklyLogAnalyticsResponse;
}

export const WeeklyLogOverviewTab: React.FC<WeeklyLogOverviewTabProps> = ({
  analyticsData,
}) => {
  const { t } = useTranslation();
  const isRTL = useAppSelector(selectIsRTL);
  const config = useChartConfig();

  const overviewData = [
    {
      label: t('analytics.prayerLogs'),
      value: analyticsData.prayerLogs.count,
      color: config.colors.primary,
    },
    {
      label: t('analytics.wakeUpLogs'),
      value: analyticsData.wakeUpLogs.count,
      color: config.colors.secondary,
    },
    {
      label: t('analytics.notPrayerLogs'),
      value: analyticsData.notPrayerLogs.count,
      color: config.colors.primary,
    },
    {
      label: t('analytics.notWakeUpLogs'),
      value: analyticsData.notWakeUpLogs.count,
      color: config.colors.secondary,
    },
  ];

  const genderComparisonData = [
    {
      name: t('analytics.prayerLogs'),
      MALE:
        analyticsData.prayerLogs.genderDistribution?.find(
          (g) => g.value?.toLowerCase() === 'male'
        )?.count || 0,
      FEMALE:
        analyticsData.prayerLogs.genderDistribution?.find(
          (g) => g.value?.toLowerCase() === 'female'
        )?.count || 0,
    },
    {
      name: t('analytics.wakeUpLogs'),
      MALE:
        analyticsData.wakeUpLogs.genderDistribution?.find(
          (g) => g.value?.toLowerCase() === 'male'
        )?.count || 0,
      FEMALE:
        analyticsData.wakeUpLogs.genderDistribution?.find(
          (g) => g.value?.toLowerCase() === 'female'
        )?.count || 0,
    },
    {
      name: t('analytics.notPrayerLogs'),
      MALE:
        analyticsData.notPrayerLogs.genderDistribution?.find(
          (g) => g.value?.toLowerCase() === 'male'
        )?.count || 0,
      FEMALE:
        analyticsData.notPrayerLogs.genderDistribution?.find(
          (g) => g.value?.toLowerCase() === 'female'
        )?.count || 0,
    },
    {
      name: t('analytics.notWakeUpLogs'),
      MALE:
        analyticsData.notWakeUpLogs.genderDistribution?.find(
          (g) => g.value?.toLowerCase() === 'male'
        )?.count || 0,
      FEMALE:
        analyticsData.notWakeUpLogs.genderDistribution?.find(
          (g) => g.value?.toLowerCase() === 'female'
        )?.count || 0,
    },
  ];

  return (
    <ChartContainer>
      {/* Header */}
      <div className="p-4 text-center">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('analytics.title')}
        </h2>
        <p className="text-sm text-gray-700">{t('analytics.subtitle')}</p>
      </div>

      {/* Content */}
      <ChartContent>
        <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {overviewData.map((item, index) => (
            <div
              key={index}
              className="rounded-lg bg-white p-4 text-black shadow"
            >
              <h3 className="text-sm font-medium text-black">{item.label}</h3>
              <p className="mt-1 text-2xl font-semibold text-black">
                {item.value}
              </p>
            </div>
          ))}
        </div>

        <FooterDivider />
        {/* Gender Comparison Chart */}
        <div className="mt-8">
          <div className="p-4 text-center">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('analytics.labels.genderDistribution')}
            </h2>
            <p className="text-sm text-gray-700">
              {t('analytics.labels.activityDistributionByGender')}
            </p>
          </div>
          <ResponsiveContainer
            width="100%"
            height={360}
            style={config.styles.noOutline}
          >
            <BarChart
              data={genderComparisonData}
              margin={{
                top: 25,
                right: isRTL ? 20 : 20,
                left: isRTL ? -20 : 0,
              }}
              barSize={20}
              style={config.styles.noOutline}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={config.colors.grid}
              />
              <XAxis
                dataKey="name"
                scale="point"
                padding={{ left: 75, right: 75 }}
                {...config.axis}
              />
              <YAxis {...config.axis} />
              <Tooltip
                {...config.tooltip}
                formatter={(value, name) => {
                  return [value, name];
                }}
                labelFormatter={(label) => `${label}`}
              />
              <Legend {...config.legend} />
              <Bar
                dataKey="MALE"
                name={t('demographics.gender.male')}
                fill={config.colors.primary}
                style={config.styles.noOutline}
                radius={[5, 5, 0, 0]}
                activeBar={{ fill: config.colors.primary, opacity: 0.6 }}
              />
              <Bar
                dataKey="FEMALE"
                name={t('demographics.gender.female')}
                fill={config.colors.secondary}
                style={config.styles.noOutline}
                radius={[5, 5, 0, 0]}
                activeBar={{ fill: config.colors.secondary, opacity: 0.6 }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartContent>

      {/* Footer */}
      <ChartFooter>{t('analytics.allActivities')}</ChartFooter>
    </ChartContainer>
  );
};
